import { useMemo, useState } from 'react'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { showErrorAlert } from '@/shared/lib/errors'
import { Button } from '@/shared/ui'
import { Modal } from '@/shared/ui/BaseModal'
import type { CsatActivePrompt } from '@/types/csat'

import { submitCsat, today } from './api'
import { useCsatStore, promptKey } from './store'

const SCORES = [1, 2, 3, 4, 5]

const resolveKey = (prompt: CsatActivePrompt) => {
  if (prompt.topic) return promptKey.topic(prompt.topic.id)
  if (prompt.campaign.kind === 'final')
    return promptKey.final(prompt.campaign.id, today())
  return promptKey.interaction(
    prompt.campaign.id,
    prompt.contextType ?? '',
    prompt.contextId ?? 0
  )
}

export const CsatPrompt = () => {
  const { digitalProductId } = useDigitalProductContext()
  const { refetchProgress } = useUserProgress()
  const { activePrompt, dismiss } = useCsatStore()

  const [score, setScore] = useState<number | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isFinal = activePrompt?.campaign.kind === 'final'
  const extraQuestions = useMemo(
    () => (isFinal ? activePrompt?.campaign.extra_questions ?? [] : []),
    [isFinal, activePrompt]
  )

  if (!activePrompt) return null

  const key = resolveKey(activePrompt)

  const missingRequired = extraQuestions.some(
    q => q.required && !answers[q.title]?.trim()
  )
  const canSubmit = score != null && !missingRequired && !isSubmitting

  const handleClose = () => {
    setScore(null)
    setAnswers({})
    dismiss(key)
  }

  const handleSubmit = async () => {
    if (score == null) return
    setIsSubmitting(true)
    try {
      await submitCsat(digitalProductId, {
        csat_campaign_id: activePrompt.campaign.id,
        csat_topic_id: activePrompt.topic?.id ?? null,
        score,
        context_type: activePrompt.contextType ?? null,
        context_id: activePrompt.contextId ?? null,
        extra_answers: answers,
        answered_on: today()
      })
      // Atualiza o progress local (que passa a conter a resposta) e encerra o
      // ciclo da sessão para não reapresentar o mesmo prompt.
      refetchProgress()
      setScore(null)
      setAnswers({})
      dismiss(key)
    } catch (error) {
      showErrorAlert(error)
    }
    setIsSubmitting(false)
  }

  return (
    <Modal show centered onHide={handleClose}>
      <Modal.Header onHide={undefined}>
        <Modal.Title className="mb-0">{activePrompt.campaign.title}</Modal.Title>
        {activePrompt.topic && (
          <Modal.Subtitle>{activePrompt.topic.name}</Modal.Subtitle>
        )}
      </Modal.Header>
      <Modal.Body noPaddings={false} customPaddings={false}>
        <p className="text-center text-muted-foreground mb-4 text-base">
          {activePrompt.campaign.prompt_text}
        </p>

        <div className="flex justify-center gap-1 mb-4">
          {SCORES.map(value => (
            <button
              key={value}
              type="button"
              className="inline-flex items-center justify-center p-0 border-0 bg-transparent cursor-pointer"
              onClick={() => setScore(value)}
              style={{ fontSize: '2rem', lineHeight: 1 }}
            >
              <span
                style={{
                  color: score != null && value <= score ? '#f5a623' : '#d4d4d8',
                  transition: 'color 0.15s'
                }}
              >
                ★
              </span>
            </button>
          ))}
        </div>

        {isFinal && score != null && extraQuestions.length > 0 && (
          <div className="flex flex-col gap-3 mt-2">
            {extraQuestions.map(question => (
              <div key={question.title}>
                <label className="mb-2 block text-sm font-semibold text-gray-700">
                  {question.title}
                  {question.required && (
                    <span className="text-danger ml-1">*</span>
                  )}
                </label>
                {question.description && (
                  <div className="text-muted-foreground text-xs mb-1">
                    {question.description}
                  </div>
                )}
                {question.kind === 'textarea' ? (
                  <textarea
                    className="w-full rounded border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    rows={3}
                    placeholder={question.placeholder}
                    value={answers[question.title] ?? ''}
                    onChange={e =>
                      setAnswers({
                        ...answers,
                        [question.title]: e.target.value
                      })
                    }
                  />
                ) : (
                  <input
                    type="text"
                    className="w-full rounded border border-gray-300 bg-gray-100 px-4 py-2.5 text-sm text-gray-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                    placeholder={question.placeholder}
                    value={answers[question.title] ?? ''}
                    onChange={e =>
                      setAnswers({
                        ...answers,
                        [question.title]: e.target.value
                      })
                    }
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer noPaddings={false}>
        <Button
          type="button"
          variant="light"
          onClick={handleClose}
          disabled={isSubmitting}
          showTrailingIcon={false}
        >
          Agora não
        </Button>
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          disabled={!canSubmit}
          loading={isSubmitting}
          showTrailingIcon={false}
        >
          {isSubmitting ? 'Enviando...' : 'Enviar avaliação'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
